<template>
  <div>
    <navbar/>
    <error-panel showInPopup="false" />

    <!-- Pocket Mode Banner -->
    <section v-if="isPocketMode" class=" mb-0 py-5 pocket-banner">
      <div class="container text-center alert alert-warning">
        <h3 class="mb-3">🚨 Pocket shut down on <strong>November 12, 2025</strong>! 🚨</h3>

        <div class="countdown-display mb-5">
          <h4>You have <strong>{{ daysRemaining }}</strong> days left to export your bookmarks.</h4>
        </div>

        <p class="lead mb-1">
          Pocket will <strong>permanently delete all user data</strong> - your saved articles,
          tags after the shutdown date.
        </p>

        <p class="lead mb-4">
          Don’t lose them! Export your Pocket data now to preserve your reading list safely.
        </p>

        <div class="mt-3">
          <a href="https://getpocket.com/export" target="_blank" class="btn btn-danger btn-lg mr-2">
            <i class="bi bi-box-arrow-down mr-1"></i> Go to Pocket Export
          </a>
        </div>

        <small class="text-muted d-block mt-4">
          We’re not affiliated with Pocket - we just want to help you keep your bookmarks safe.
        </small>
      </div>

      <!-- Export Instructions -->
      <section v-if="isPocketMode" class="container mt-5 pocket-instructions alert-info p-4 alert">
        <div class="container">
          <h4 class="mb-4">📤 How to Export Your Pocket Data</h4>
          <ol class="lead">
            <li class="mb-3">
              <strong>Check your account email.</strong><br />
              Make sure your Pocket or Mozilla account uses a valid, active email address before initiating an export.
              Your data export will be sent there.
            </li>
            <li class="mb-3">
              <strong>Go to <a href="https://getpocket.com/export" target="_blank">getpocket.com/export</a>.</strong><br />
              Check the email address shown in the top left of the page.<br />
              <em>Note:</em> If your email is incorrect or inaccessible, update it before requesting your export.
            </li>
            <li class="mb-3">
              <strong>Export your Pocket data.</strong><br />
              Click the <em>“Export CSV file”</em> button. You’ll see a message confirming your export request.
              It can take up to 24 hours for your data to be generated and sent to your email.
              <br />
              💡 <em>Tip:</em> Check your spam or junk folder if you don’t see the export email after 24 hours.
            </li>
            <li class="mb-3">
              <strong>Wait until you receive the file before deleting anything.</strong><br />
              Do <em>not</em> delete your Pocket content until you’ve received the export file — Pocket cannot restore deleted data.
            </li>

          </ol>
          <div class="text-center pt-3">
            <small class="text-muted">For more detailed instructions, visit <a href="https://support.mozilla.org/en-US/kb/exporting-your-pocket-list">https://support.mozilla.org/en-US/kb/exporting-your-pocket-list</a></small>
          </div>
        </div>
      </section>

      <!-- Import Explanation -->
      <section v-if="isPocketMode" class="container mt-5 pocket-import alert-info p-3 alert">
        <div class="container">
          <h4 class="mb-3">📥 Importing into “just bookmark it!”</h4>
          <p class="lead">
            Once you’ve downloaded your Pocket export file, you’ll be able to import it directly into
            <strong><a href="/">just bookmark it!</a></strong>, our tag-based social bookmarking site (and iOS app) with a social twist. The import process will:
          </p>
          <ul class="lead">
            <li>✅ Preserve all your saved URLs</li>
            <li>✅ Keep your tags and read/unread status</li>
            <li>⚠️ <em>Reading progress (how far you were in an article) cannot be imported</em> — Pocket’s export file doesn’t include that data</li>
          </ul>

          <div class="mt-3 text-center p-3">
            <a href="#" data-toggle="modal" data-target="#registerEmailModal" class="mt-3 btn btn-cta btn-cta-large btn-lg">Interested? Start the free trial!</a>
          </div>
        </div>
      </section>
    </section>

    <!-- Regular hero (for non-pocket visitors) -->

    <section v-else class="hero py-7">
      <div class="container">
        <div class="row align-items-center">
          <div class="col-md-6 hero-copy">
            <h1 class="display-4">Save, organize, and rediscover the web</h1>
            <p class="lead my-4">
              <strong>just bookmark it!</strong> is a privacy-friendly, flexible bookmarking app with powerful search and social features.
              Keep your bookmarks in one place and access them from anywhere.
            </p>

            <div class="text-center">
              <a class="btn btn-cta btn-cta-large btn-lg" href="#" data-toggle="modal" data-target="#registerEmailModal">Interested? Start the free trial!</a>
            </div>

            <div class="floating-section-nav d-flex">
              <a class="nav-pill" href="#features" @click.prevent="scrollToDiscover('features')">features</a>
              <a class="nav-pill" href="#gallery" @click.prevent="scrollToDiscover('gallery')">gallery</a>
              <a class="nav-pill" href="#faq" @click.prevent="scrollToDiscover('faq')">faq</a>
              <a class="nav-pill" href="#pricing" @click.prevent="scrollToDiscover('pricing')">pricing</a>
            </div>
          </div>

          <div class="col-md-1" />
          <div class="col-md-5 hero-media d-none d-md-block">
            <div class="hero-image shadow-soft">
              <img src="gallery/desktop-screenshot-all-bookmarks.webp" @click="showFullSizePreview($event)" alt="App screenshot" class="img-fluid">
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="py-5 use-cases">
    <div class="container text-center">
      <h3>What can you do with <i>just bookmark it?</i></h3>

      <p class="lead mt-5 container text-left" style="max-width: 600px">
        <ul class="list-unstyled lead">
          <li class="mb-1 fade-in">
            <i class="bi bi-check-circle-fill text-success mr-2"></i>
            Organize messy bookmarks into something you’ll actually use.
          </li>

          <li class="mb-1 fade-in" style="animation-delay: 0.2s;">
            <i class="bi bi-check-circle-fill text-success mr-2"></i>
            Save links without keeping hundreds of browser tabs open.
          </li>
          <li class="mb-1 fade-in" style="animation-delay: 0.4s;">
            <i class="bi bi-check-circle-fill text-success mr-2"></i>
            <span><a href="/discover">Discover</a> what your friends are saving.</span>
          </li>
          <li class="mb-1 fade-in" style="animation-delay: 0.6s;">
            <i class="bi bi-check-circle-fill text-success mr-2"></i>
            Quickly find that article, video, or cool website you saved.
          </li>
          <li class="mb-1 fade-in" style="animation-delay: 0.8s;">
            <i class="bi bi-check-circle-fill text-success mr-2"></i>
            Always have your bookmarks, no matter the device.
          </li>
          <li class="mb-1 fade-in" style="animation-delay: 1s;">
            <i class="bi bi-check-circle-fill text-success mr-2"></i>
            Collect, organize and share resources in one place.
          </li>
        </ul>
      </p>

    </div>

    </section>
    <!-- Features -->
    <section id="features" class="py-5">
      <div class="container text-center">
        <h2 class="mb-5">Features</h2>
        <div class="row">
          <div class="col-md-4 mb-4">
            <div class="card border-0 shadow-sm h-100 feature-card p-4">
              <div class="mb-3 text-primary">
                <i class="bi bi-bookmark-star-fill" style="font-size:2rem;"></i>
              </div>
              <p class="mb-0">Quickly save webpages via the drag'n'drop
                <router-link to="/help">
                Bookmarklet
                </router-link>or the
                <a href="https://apps.apple.com/us/app/just-bookmark-it/id6749245868" target="_blank">
                  iOS app
                </a>
              </p>
            </div>
          </div>
          <div class="col-md-4 mb-4">
            <div class="card border-0 shadow-sm h-100 feature-card p-4">
              <div class="mb-3 text-warning">
                <i class="bi bi-tags-fill" style="font-size:2rem;"></i>
              </div>
              <p class="mb-0">Get automatically suggested tags to organize your bookmarks</p>
            </div>
          </div>
          <div class="col-md-4 mb-4">
            <div class="card border-0 shadow-sm h-100 feature-card p-4">
              <div class="mb-3 text-info">
                <i class="bi bi-cloud-arrow-up-fill" style="font-size:2rem;"></i>
              </div>
              <p class="mb-0">Bring in your existing bookmarks from your browser or pocket account</p>
            </div>
          </div>
          <div class="col-md-4 mb-4">
            <div class="card border-0 shadow-sm h-100 feature-card p-4">
              <div class="mb-3 text-danger">
                <i class="bi bi-people-fill" style="font-size:2rem;"></i>
              </div>
              <p class="mb-0">Browse and discover public bookmarks - <a href="/discover" class="">Try it now!</a></p>
            </div>
          </div>
          <div class="col-md-4 mb-4">
            <div class="card border-0 shadow-sm h-100 feature-card p-4">
              <div class="mb-3 text-success">
                <i class="bi bi-shield-lock-fill" style="font-size:2rem;"></i>
              </div>
              <p class="mb-0">Keep bookmarks just for yourself - or share them publicly</p>
            </div>
          </div>
          <div class="col-md-4 mb-4">
            <div class="card border-0 shadow-sm h-100 feature-card p-4">
              <div class="mb-3 text-dark">
                <i class="bi bi-shield-check" style="font-size:2rem;"></i>
              </div>
              <p class="mb-0">Trust: No 3rd party trackers, scripts, AI feeds or dark patterns</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Gallery -->
    <section id="gallery" class="section">
      <div class="container text-center">
        <h2 class="mb-5">Gallery</h2>
        <div class="row mb-3">
          <div class="col-12 ">
            <div class="btn-group btn-group-toggle" data-toggle="buttons">
              <label class="btn btn-outline-primary" >
                <input type="radio"
                       name="options"
                       id="option1"
                       :checked="galleryMode == 'MOBILE'"
                        @click="galleryMode = 'MOBILE'">
                <i class="bi bi-phone-fill mr-2"></i>iOS app
              </label>
              <label class="btn btn-outline-primary" >
                <input type="radio"
                       name="options"
                       id="option2"
                       :checked="galleryMode == 'DESKTOP'"
                       @click="galleryMode = 'DESKTOP'"><i class="bi bi-window-fullscreen mr-2"></i>Desktop
              </label>
            </div>
          </div>
        </div>
        <div v-if="galleryMode == 'DESKTOP'" class="row mt-4 justify-content-center">

          <a href="gallery/desktop-screenshot-bookmarklet.webp"
             data-toggle="lightbox"
             data-gallery="example-gallery"
             class="col-sm-4">
            <img src="gallery/desktop-screenshot-bookmarklet.webp" class="img-fluid">
          </a>

          <a href="gallery/desktop-screenshot-search.webp"
             data-toggle="lightbox"
             data-gallery="example-gallery"
             class="col-sm-4">
            <img src="gallery/desktop-screenshot-search.webp" class="img-fluid">
          </a>

          <a href="gallery/desktop-screenshot-import.webp"
             data-toggle="lightbox"
             data-gallery="example-gallery"
             class="col-sm-4">
            <img src="gallery/desktop-screenshot-import.webp" class="img-fluid">
          </a>

          <a href="gallery/desktop-screenshot-discover.webp"
             data-toggle="lightbox"
             data-gallery="example-gallery"
             class="col-sm-4">
            <img src="gallery/desktop-screenshot-discover.webp" class="img-fluid">
          </a>

          <a href="gallery/desktop-screenshot-tag-cloud.webp"
             data-toggle="lightbox"
             data-gallery="example-gallery"
             class="col-sm-4">
            <img src="gallery/desktop-screenshot-tag-cloud.webp" class="img-fluid">
          </a>

          <a href="gallery/desktop-screenshot-tags.webp"
             data-toggle="lightbox"
             data-gallery="example-gallery"
             class="col-sm-4">
            <img src="gallery/desktop-screenshot-tags.webp" class="img-fluid">
          </a>

          <a href="gallery/desktop-screenshot-all-bookmarks.webp"
             data-toggle="lightbox"
             data-gallery="example-gallery"
             class="col-sm-4">
            <img src="gallery/desktop-screenshot-all-bookmarks.webp" class="img-fluid">
          </a>
        </div>

        <div v-if="galleryMode == 'MOBILE'" class="row mt-4 justify-content-center">
          <div class="row col-sm-10">
              <a href="gallery/iphone-16-all-bookmarks.webp"
               data-toggle="lightbox"
               data-gallery="example-gallery"
               class="col-sm-4">
              <img src="gallery/iphone-16-all-bookmarks.webp" class="img-fluid">
            </a>
              <a href="gallery/iphone-16-share-menu.webp"
                 data-toggle="lightbox"
                 data-gallery="example-gallery"
                 class="col-sm-4">
                <img src="gallery/iphone-16-share-menu.webp" class="img-fluid">
              </a>
              <a href="gallery/iphone-16-save-with-suggested-tags.webp"
               data-toggle="lightbox"
               data-gallery="example-gallery"
               class="col-sm-4">
              <img src="gallery/iphone-16-save-with-suggested-tags.webp" class="img-fluid">
            </a>
              <a href="gallery/iphone-16-search.webp"
               data-toggle="lightbox"
               data-gallery="example-gallery"
               class="col-sm-4">
              <img src="gallery/iphone-16-search.webp" class="img-fluid">
            </a>
              <a href="gallery/iphone-16-discover.webp"
               data-toggle="lightbox"
               data-gallery="example-gallery"
               class="col-sm-4">
              <img src="gallery/iphone-16-discover.webp" class="img-fluid">
            </a>
              <a href="gallery/iphone-16-tagged-bookmarks.webp"
                 data-toggle="lightbox"
                 data-gallery="example-gallery"
                 class="col-sm-4">
                <img src="gallery/iphone-16-tagged-bookmarks.webp" class="img-fluid">
              </a>
              <a href="gallery/iphone-16-import.webp"
                 data-toggle="lightbox"
                 data-gallery="example-gallery"
                 class="col-sm-4">
                <img src="gallery/iphone-16-import.webp" class="img-fluid">
              </a>
              <a href="gallery/iphone-16-tag-cloud.webp"
                 data-toggle="lightbox"
                 data-gallery="example-gallery"
                 class="col-sm-4">
                <img src="gallery/iphone-16-tag-cloud.webp" class="img-fluid">
              </a>
          </div>
        </div>
      </div>
    </section>

  <section id="faq" class="py-5">
    <div class="container">

      <div class="text-center mb-5">
        <h2 class="">Frequently Asked Questions</h2>
      </div>

      <div id="faqqordion" class="accordion">

        <div class="card">
          <div class="card-header" id="faq2">
            <h5 class="mb-0">
              <button class="btn btn-link collapsed text-left w-100" data-toggle="collapse" data-target="#collapse-save">
                <i class="bi bi-bookmark-plus mr-2"></i>
                How do I save bookmarks?
              </button>
            </h5>
          </div>

          <div id="collapse-save" class="collapse" data-parent="#faqqordion">
            <div class="card-body">
              Saving bookmarks is simple. You don’t need to install a browser extension -
              <b>just bookmark it!</b> works with a bookmarklet, a button you just drag to your
              browser’s bookmarks bar. When you click it, the current page can be saved.
              There’s also an iOS app available for saving webpages on your Apple
              devices. See the <a href="/help">help page</a> for more details.
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-header" id="faq1">
            <h5 class="mb-0">
              <button class="btn btn-link text-left w-100" data-toggle="collapse" data-target="#collapse1">
                <i class="bi bi-globe2 mr-2"></i>
                Can I access my bookmarks from multiple devices?
              </button>
            </h5>
          </div>

          <div id="collapse1" class="collapse" data-parent="#faqqordion">
            <div class="card-body">
              Yes. Your bookmarks are available anywhere: on your home computer,
              work laptop, tablet, or phone. Everything syncs automatically and
              instantly without any setup.
            </div>
          </div>
        </div>



        <div class="card">
          <div class="card-header" id="faq2">
            <h5 class="mb-0">
              <button class="btn btn-link collapsed text-left w-100" data-toggle="collapse" data-target="#collapse2">
                <i class="bi bi-arrow-repeat mr-2"></i>
                Do I need to use the same browser on my devices?
              </button>
            </h5>
          </div>

          <div id="collapse2" class="collapse" data-parent="#faqqordion">
            <div class="card-body">
              No. Unlike browser bookmarks that are tied to a specific browser,
              <b>just bookmark it!</b> works independently. You can use Safari on your
              phone, Chrome on your laptop, and Firefox on another computer -
              your bookmarks remain available everywhere.
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-header" id="faq3">
            <h5 class="mb-0">
              <button class="btn btn-link collapsed text-left w-100" data-toggle="collapse" data-target="#collapse3">
                <i class="bi bi-tags mr-2"></i>
                How is organization better than browser folders?
              </button>
            </h5>
          </div>

          <div id="collapse3" class="collapse" data-parent="#faqqordion">
            <div class="card-body">
              Traditional bookmarks use rigid folder structures. With <b>just bookmark it!</b>,
              you can assign multiple tags to a bookmark, making it much flexible
              to organize and easier to retrieve links later.
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-header" id="faq4">
            <h5 class="mb-0">
              <button class="btn btn-link collapsed text-left w-100" data-toggle="collapse" data-target="#collapse4">
                <i class="bi bi-search mr-2"></i>
                Can I see links from other users?
              </button>
            </h5>
          </div>

          <div id="collapse4" class="collapse" data-parent="#faqqordion">
            <div class="card-body">
              Yes. You can explore public bookmarks by other users,
              <a href="/discover">discover</a> interesting resources, and see what your friends are saving.
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-header" id="faq5">
            <h5 class="mb-0">
              <button class="btn btn-link collapsed text-left w-100" data-toggle="collapse" data-target="#collapse5">
                <i class="bi bi-share mr-2"></i>
                Can I share my bookmarks with others?
              </button>
            </h5>
          </div>

          <div id="collapse5" class="collapse" data-parent="#faqqordion">
            <div class="card-body">
              Absolutely. You can easily share individual bookmarks or entire
              collections with a simple link. This makes it easy to collaborate,
              recommend resources, or curate useful lists.
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-header" id="faq5">
            <h5 class="mb-0">
              <button class="btn btn-link collapsed text-left w-100" data-toggle="collapse" data-target="#collapse_import">
                <i class="bi bi-cloud-arrow-up mr-2"></i>
                Can I bring in my existing bookmarks?
              </button>
            </h5>
          </div>

          <div id="collapse_import" class="collapse" data-parent="#faqqordion">
            <div class="card-body">
              Yes. You can import your browser's bookmarks (all major browsers are supported), or your exported <a href="https://getpocket.com/home">pocket</a> links.
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-header" id="faq6">
            <h5 class="mb-0">
              <button class="btn btn-link collapsed text-left w-100" data-toggle="collapse" data-target="#collapse6">
                <i class="bi bi-lightning-charge mr-2"></i>
                What is the future of <b>just bookmark it!</b>?
              </button>
            </h5>
          </div>

          <div id="collapse6" class="collapse" data-parent="#faqqordion">
            <div class="card-body">
              Upcoming features include automatic dead-link detection,
              smarter bookmark recommendations, and additional discovery tools
              to help you find useful content faster. <br/><br/> If you are missing a specific feature for your workflow, <a href="mailto:support@justbookmark.it?subject=Feature%20suggestion">contact us</a>.
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

    <section id="pricing" class="py-5">
      <div class="container text-center">
        <h2 class="mb-5" >Pricing</h2>

        <div class="row">

          <div class="col-md-1 mb-4">
          </div>

          <div class="col-md-5 mb-4">
            <div class="card h-100 border-primary">
              <div class="card-body text-center">
                <h5 class="card-title text-primary pb-4">Standard subscription</h5>
                <p class="card-text pb-0">
                  <span class="text-muted"><i class="bi bi-1-circle-fill text-success mr-2"></i>sign up for a free trial</span> <br/>
                  <span class="text-muted"><i class="bi bi-2-circle-fill text-success mr-2"></i>try all features for <b>3 days</b></span> <br/>
                  <span class="text-muted"><i class="bi bi-3-circle-fill text-success mr-2"></i>after the trial, subscribe to our monthly plan</span> <br/>
                </p>

                <p>
                  <span style="font-size: 1.3em" class="text-primary"><b>$1.79</b> / month</span> <br/>  <small class="text-muted">(VAT or local sales taxes may apply)</small>
                </p>
                <a class="mt-2 btn btn-cta btn-cta-small"
                   data-toggle="modal"
                   data-target="#registerEmailModal">Start trial</a>
              </div>
            </div>
          </div>

          <div class="col-md-5 mb-4">
            <div class="card h-100 border-primary">
              <div class="card-body text-center">
                <h5 class="card-title text-primary pb-4">Academic program</h5>
                <p class="card-text pb-0 text-muted">
                50% discount for students, professors, and researchers.
                </p>

                <p class="mt-3">
                  <span style="font-size: 1.5em" class="text-primary"><b>$0.89</b> / month</span><br/>
                  <span class="text-muted">instead of</span>  <span class="text-danger"><del><b>$1.79</b> / month</del></span> <br/>

                  <small class="text-muted">(VAT or local sales taxes may apply)</small>
                </p>

                <a href="/academic-program" class="btn btn-primary btn-cta-small">Apply for discount</a>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>

    <!-- Manifesto -->
    <section class="section mb-4 manifesto">
      <div class="container card col-sm-7 col-11">
        <h2 class="text-center mb-5 mt-5" id="why">A note from the developer</h2>
        <p class="text-center card-body">
          Hi, I’m Peter. I’m an independent developer based in Hungary, and I’m building <b>"just bookmark it!"</b> on my own.<br/><br/>

          I originally built this tool for a simple, selfish reason: I personally needed it.
          I wanted a place to collect and organize the small pieces of the internet I wanted to keep - recipes
          to try out, cartoons for my kid, obscure music clips, or blog posts I wanted to revisit someday.
          And I wanted them all to be accessible whether on my phone or at my computer.<br/><br/>

          Before <b>"just bookmark it!"</b>, I spent years writing software for banks and insurance companies. In that world, reliability,
          security, and correctness aren't just buzzwords; they are strict requirements. That background still influences
          how I build things. <b>"just bookmark it!"</b> might look like a simple tool, but it isn’t a quick side project thrown together over a weekend.
          It is built with the same attention to stability and data safety that enterprise systems require.<br/><br/>

          I’m building this without investors or corporate backing.
          The goal is simply to make a reliable tool that does its job
          well and respects the people who use it.<br/><br/>

          If you give it a try and something feels confusing, annoying, or just unnecessary, please let me know.
          I don't have a product team or analytics department... Honest feedback from real people is the only way
          I shape what comes next.<br/><br/>

          Thanks for stopping by.<br/><br/>
        </p>
        <p class="card-body">
            — Peter<br/>
            Developer of <b>just bookmark it!</b><br/>
            Budapest, Hungary<br/>
        </p>
      </div>
    </section>
    <div v-if="fullScreenImageUrl !== null"
        @click.self="closeFullScreenImage"
        class="fullscreen-image-overlay">
            <span
                @click="closeFullScreenImage"
                class="close-btn" >&times;
            </span>
            <img
                :src="fullScreenImageUrl"
                class="fullscreen-image img-fluid rounded"
            />
        </div>
    <tlfooter/>
</div>
</template>

<script>
import navbar from "@/components/NavBar";
import tlfooter from "@/components/Footer";
import ErrorPanel from "@/components/ErrorPanel";
import BackendClient from "@/rest/backend-client";

export default {
  name: "Home",
  components: {
    ErrorPanel, navbar, tlfooter
  },
  props: ["isPocketMode"],
  data() {
    return {
      notes: "",
      backendClient: new BackendClient(),
      daysRemaining: 0,
      galleryMode: this.isMobile() ? 'MOBILE' : 'DESKTOP',
      fullScreenImageUrl: null
    };
  },
  mounted() {
    this.initEkkoLightBox();
    this.setUpSectionBackground();
    this.calculateDaysRemaining();
  },
  methods: {
      setUpSectionBackground() {
        document.querySelectorAll("section").forEach(
            (item, index) => {
              item.classList.add(index % 2 == 1 ? "bg-white" : "bg-light")
        });
      },
      initEkkoLightBox() {
        let ekkoLightBoxScript = document.createElement('script')
        ekkoLightBoxScript.setAttribute('src', 'https://cdnjs.cloudflare.com/ajax/libs/ekko-lightbox/5.3.0/ekko-lightbox.min.js')
        document.body.appendChild(ekkoLightBoxScript)

        const ekkoLightBoxCss = document.createElement('link')
        ekkoLightBoxCss.setAttribute('href', 'https://cdnjs.cloudflare.com/ajax/libs/ekko-lightbox/5.3.0/ekko-lightbox.css')
        ekkoLightBoxCss.setAttribute('rel', 'stylesheet');

        document.head.appendChild(ekkoLightBoxCss);

        // eslint-disable-next-line
        $(document).on('click', '[data-toggle="lightbox"]', function(event) {
          event.preventDefault();
          // eslint-disable-next-line
          $(this).ekkoLightbox({
              alwaysShowClose: true,
              leftArrow: '<span class="gallery-arrow"> ❮ </span>',
              rightArrow: '<span class="gallery-arrow"> ❯ </span>'
          });
        });
      },
    calculateDaysRemaining() {
      const shutdownDate = new Date("2025-11-12T00:00:00Z");
      const now = new Date();
      const diff = shutdownDate - now;
      this.daysRemaining = Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
    },
    isMobile() {
        return /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(navigator.userAgent);
    },
    scrollToDiscover(id) {
      document.querySelector('#'+id)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      });
    },
    showFullSizePreview(event){
      this.fullScreenImageUrl = event.srcElement.getAttribute('src');
    },
    closeFullScreenImage() {
      this.fullScreenImageUrl = null;
    }
  }
};
</script>

<style>

body {
  background: linear-gradient(180deg, #fbfbfc 0%, #f7f9fb 100%);
  color: var(--dark);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial;
}

.hero {
  background: linear-gradient(135deg, rgba(0,123,255,0.06) 0%, rgba(255,193,7,0.05) 100%);
  padding-top: 4rem;
  padding-bottom: 4rem;
}

.hero-copy h1 {
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--dark);
}

.hero-copy p.lead {
  font-size: 1.125rem;
  color: var(--muted);
}

.hero-image {
  background: white;
  border-radius: 18px;
  padding: 18px;
  display: inline-block;
  transform: translateY(3px);
}

.shadow-soft {
  box-shadow: 0 10px 30px rgba(51,51,51,0.08);
}

.pocket-banner {
background: #fff3cd;
border-bottom: 2px solid #f1e28a;
}
.pocket-banner h3 {
font-weight: 700;
}
.pocket-banner .btn {
border-radius: 0.75rem;
}
.countdown-display {
font-size: 1.5rem;
font-weight: 600;
}
.pocket-instructions ol {
counter-reset: item;
padding-left: 1.5rem;
}
.pocket-instructions li::marker {
font-weight: bold;
}
.pocket-import ul {
list-style-type: none;
padding-left: 0;
}
.pocket-import li {
margin-bottom: 0.5rem;
}
  .feature-card {
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    border-radius: 1rem;
  }
  .feature-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 20px rgba(0,0,0,0.1);
  }

  .input-group-text {
    width: 5em;
  }

 .support-text {
    font-size: 0.7em
  }

  .tl-check-icon {
    color: forestgreen !important;
  }

  span.gallery-arrow {
    color:#ffc107;
    font-size: 1.5em;
  }

  .ekko-lightbox .modal-footer {
    justify-content: center;
    text-align: center!important;
  }

  .use-cases ul li {
    display: flex;
    align-items: center;
    opacity: 0;
    transform: translateY(10px);
    font-size: 1em;
  }

  .use-cases ul i {
    flex-shrink: 0;
    font-size: 1.25rem;
  }

  /* Fade-in animation */
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
      }
  }

  .fade-in {
    animation: fadeInUp 0.6s ease forwards;
  }

  @keyframes pulse {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.03);
    }
    100% {
      transform: scale(1);
    }
  }

  .fragment-link {
    font-size: 1.1em;
  }

/* Floating section navigation */
.floating-section-nav {
  position: fixed;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1030;
  background: rgba(255,255,255,0.75);
  backdrop-filter: blur(10px);
  border-radius: 999px;
  padding: 6px 10px;
  box-shadow: 0 8px 30px rgba(0,0,0,0.08);
  border: 1px solid rgba(0,0,0,0.05);
}

.nav-pill {
  padding: 6px 14px;
  margin: 0 4px;
  border-radius: 999px;
  font-size: 0.9rem;
  font-weight: 500;
  color: #333;
  text-decoration: none;
  transition: all 0.25s ease;
}

.nav-pill:hover {
  background: #007bff;
  color: white;
  transform: translateY(-1px);
  text-decoration: none;
}

.nav-pill.active {
  background: linear-gradient(90deg,#ffc107,#ffb000);
  color: rgba(0,0,0,0.9);
}

</style>
